// Saved addresses — the customer's own book (PUT/DELETE customer/me/
// addresses/{id}, the staff door's shape scoped to this identity's row).
// A new address is found through the store's geocoder, so it carries the
// point the delivery zones match on; the checkout's delivery step offers
// the book, default first. The client mints the address id.

import { useState } from 'react';
import { useParams } from 'react-router';
import {
  ApiError, addressText, deleteMyAddress, putMyAddress, searchAddress,
  type AddressCandidate, type AddressInput, type CustomerAddress,
} from '../../lib/api';
import { AccountHead, NeedsProfile, accountPath, useMe } from './shared';

const newAddressId = () => `addr_${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}`;
const nameOf = (a: CustomerAddress) => a.label || a.addressLine;
const inputOf = (a: CustomerAddress): AddressInput => ({ label: a.label, addressLine: a.addressLine, suburb: a.suburb, state: a.state, postcode: a.postcode, lat: a.lat, lng: a.lng, notes: a.notes, isDefault: a.isDefault });

export function Addresses() {
  const { rid, sid } = useParams() as { rid: string; sid: string };
  const { me, setMe, error: loadError } = useMe(rid);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<AddressCandidate[] | null>(null);
  const [picked, setPicked] = useState<AddressCandidate | null>(null);
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [asDefault, setAsDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(write: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await write();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  function startAdding() {
    setAdding(true);
    setQuery('');
    setCandidates(null);
    setPicked(null);
    setLabel('');
    setNotes('');
    setAsDefault(!me?.addresses.length); // the first address is the default
  }

  const find = () => run(async () => {
    if (query.trim().length < 4) return;
    setCandidates(null);
    setPicked(null);
    setCandidates(await searchAddress(rid, sid, query.trim()));
  });

  const save = () => run(async () => {
    if (!picked) return;
    const a: AddressInput = { label: label.trim() || null, addressLine: picked.addressLine, suburb: picked.suburb, state: picked.state, postcode: picked.postcode, lat: picked.lat, lng: picked.lng, notes: notes.trim() || null, isDefault: asDefault };
    setMe(await putMyAddress(rid, newAddressId(), a));
    setAdding(false);
  });

  const makeDefault = (a: CustomerAddress) => run(async () => setMe(await putMyAddress(rid, a.addressId, { ...inputOf(a), isDefault: true })));
  const remove = (a: CustomerAddress) => run(async () => setMe(await deleteMyAddress(rid, a.addressId)));

  return (
    <div className="co-page">
      <AccountHead back={accountPath(rid, sid)} backLabel="Back to your account" kicker="Your account" title="Saved addresses" />
      {loadError && <div className="co-err acct-err" role="alert">{loadError}</div>}
      {me === undefined && !loadError && <div className="loading">Looking…</div>}
      {me === null && <NeedsProfile rid={rid} sid={sid}>Addresses are saved to your profile — set it up first.</NeedsProfile>}
      {me && (
        <>
          <div data-section="addresses" data-count={me.addresses.length}>
            {me.addresses.length === 0 && !adding && <section className="co-sec"><p className="acct-quiet">No saved addresses yet. Save one and delivery checkout picks it up.</p></section>}
            {me.addresses.map((a) => (
              <section className="co-sec acct-addr" key={a.addressId} data-address={a.addressId} data-default={a.isDefault}>
                <div className="acct-addr-head">
                  <span className="acct-addr-label">{nameOf(a)}</span>
                  {a.isDefault && <span className="acct-badge">Default</span>}
                </div>
                <div className="acct-addr-line">{addressText(a)}</div>
                {a.notes && <div className="co-hint">{a.notes}</div>}
                <div className="acct-actions">
                  {!a.isDefault && <button className="linky" aria-label={`Make ${nameOf(a)} the default`} onClick={() => makeDefault(a)} disabled={busy}>Make default</button>}
                  <button className="linky acct-danger" aria-label={`Remove ${nameOf(a)}`} onClick={() => remove(a)} disabled={busy}>Remove</button>
                </div>
              </section>
            ))}
          </div>

          {adding ? (
            <section className="co-sec" data-section="new-address">
              <h2>New address</h2>
              {!picked ? (
                <>
                  <div className="co-row2">
                    <input className="co-input" placeholder="Street address" aria-label="Find an address" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && find()} />
                    <button className="co-apply" onClick={find} disabled={busy || query.trim().length < 4}>{busy ? '…' : 'Find'}</button>
                  </div>
                  {candidates?.map((c) => (
                    <button key={c.formatted} className="optrow" aria-label={`Use ${c.formatted}`} onClick={() => setPicked(c)}><span className="rad" />{c.formatted}</button>
                  ))}
                  {candidates?.length === 0 && <div className="co-err">Couldn't find that address — try adding the suburb.</div>}
                </>
              ) : (
                <>
                  <div className="co-offer-ok" data-picked={picked.formatted}>
                    {picked.formatted}
                    <button onClick={() => { setPicked(null); setCandidates(null); }}>change</button>
                  </div>
                  <input className="co-input acct-gap" placeholder="Label — Home, Work… (optional)" aria-label="Address label" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40} />
                  <input className="co-input" placeholder="Notes for the driver (optional)" aria-label="Delivery notes" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={200} />
                  <button className="optrow" aria-label="Make this my default" aria-pressed={asDefault} onClick={() => setAsDefault(!asDefault)}>
                    <span className={'chk' + (asDefault ? ' on' : '')} />
                    Make this my default
                  </button>
                  <button className="co-place acct-gap" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save address'}</button>
                </>
              )}
              <button className="linky acct-cancel" onClick={() => setAdding(false)}>Cancel</button>
            </section>
          ) : (
            <div className="acct-pad">
              <button className="co-apply acct-add" onClick={startAdding}>+ Add an address</button>
            </div>
          )}
          {error && <div className="co-err acct-err" role="alert">{error}</div>}
        </>
      )}
    </div>
  );
}
